import React from 'react';
import type {
  FmTableActionLabels,
  FmTableColumn,
  FmTableProps,
  FmTableResolvedGroup,
} from '../../types/fm-table';
import { FmBanner } from './FmBanner';
import { FmDataValue } from './FmDataValue';
import { FmEmpty, FmSkeleton } from './FmFeedback';
import { FmRowActions } from './FmRowActions';
import {
  columnLabel,
  cx,
  isGroupRow,
  shouldRenderSummary,
  type FmTableInternalRow,
  type SortState,
} from './FmTable.utils';
import { columnClassName } from './FmTable.styles';
import { isFmTableStructuredCell, renderFmTableCell } from './FmTableCell';

export interface FmTableA11yLabels {
  sortAscending: string;
  sortDescending: string;
  clearSort: string;
  expandRow: string;
  collapseRow: string;
}

function isEmptyCell(value: React.ReactNode): boolean {
  return value === null || value === undefined || value === '';
}

function renderCellValue(value: React.ReactNode, emptyCellLabel: string | undefined): React.ReactNode {
  if (!isEmptyCell(value)) return value;
  return <span className="fm-table__empty-cell" aria-label={emptyCellLabel}>{'\u2014'}</span>;
}

function formatColumnLabel(template: string, label: string): string {
  return template.replace('{{label}}', label);
}

function resolveDataValueRule<T>(row: T, col: FmTableColumn<T>) {
  if (!col.dataValue) return null;
  return typeof col.dataValue === 'function' ? col.dataValue(row) : col.dataValue;
}

function resolveDataValueProps<T>(row: T, col: FmTableColumn<T>) {
  if (!col.dataValueProps) return {};
  return typeof col.dataValueProps === 'function'
    ? col.dataValueProps(row)
    : col.dataValueProps;
}

function renderGroupHeader<T>(
  group: FmTableResolvedGroup<T>,
  groupHeader: FmTableProps<T>['groupHeader'],
  groupCountLabel: FmTableProps<T>['groupCountLabel'],
): React.ReactNode {
  return (
    <div className="fm-table__group-header" data-testid="fm-table-group-header">
      <span className="fm-table__group-label">
        {groupHeader ? groupHeader(group) : group.label}
      </span>
      <span className="fm-table__group-count">
        {groupCountLabel ? groupCountLabel(group.rows.length, group) : group.rows.length}
      </span>
    </div>
  );
}

export interface HeaderCellProps<T> {
  col: FmTableColumn<T>;
  canSort: boolean;
  effectiveSortState: SortState;
  a11yLabels: FmTableA11yLabels;
  onSort: (col: FmTableColumn<T>) => void;
}

export function TableHeaderCell<T>({
  col,
  canSort,
  effectiveSortState,
  a11yLabels,
  onSort,
}: HeaderCellProps<T>): React.ReactElement {
  const label = columnLabel(col.label ?? col.header, col.key);
  const direction = effectiveSortState?.key === col.key ? effectiveSortState.direction : null;
  const sortable = Boolean(col.sortable && canSort);
  const ariaSort = sortable ? (direction ?? 'none') : undefined;
  const sortLabel = direction === 'ascending'
    ? formatColumnLabel(a11yLabels.sortDescending, label)
    : direction === 'descending'
      ? formatColumnLabel(a11yLabels.clearSort, label)
      : formatColumnLabel(a11yLabels.sortAscending, label);

  return (
    <th
      key={col.key}
      scope="col"
      className={columnClassName(col, 'fm-table__cell fm-table__header-cell')}
      style={{ textAlign: col.numeric ? 'right' : col.align }}
      aria-sort={ariaSort}
    >
      {sortable ? (
        <button type="button" className="fm-table__sort-button" onClick={() => onSort(col)} aria-label={sortLabel}>
          <span className="fm-table__sort-label">{col.header}</span>
          <span className="fm-table__sort-indicator" aria-hidden="true">
            {direction === 'ascending' ? '\u25B2' : direction === 'descending' ? '\u25BC' : '\u2195'}
          </span>
        </button>
      ) : col.header}
    </th>
  );
}

export interface CellProps<T> {
  row: T;
  col: FmTableColumn<T>;
  usesLabelledCells: boolean;
  emptyCellLabel?: string;
  locale?: string;
  actionLabels?: FmTableActionLabels;
}

function resolveCellValue<T>(
  row: T,
  col: FmTableColumn<T>,
  actionLabels: FmTableActionLabels | undefined,
  emptyLabel: string,
  locale: string | undefined,
): React.ReactNode {
  if (col.actions) {
    const actions = col.actions(row);
    // A row without actions still holds the rail's height, so a table whose
    // rows carry actions on some rows only keeps one row height.
    if (actions.length === 0) {
      return <span className="fm-row-actions fm-row-actions--empty" aria-hidden="true" />;
    }
    return (
      <FmRowActions
        actions={actions}
        policy={col.actionPolicy ?? 'auto'}
        label={actionLabels?.rowActions ?? ''}
        overflowLabel={actionLabels?.moreActions ?? ''}
      />
    );
  }
  const dataValueRule = resolveDataValueRule(row, col);
  if (dataValueRule) return renderDataValue(row, col, dataValueRule);
  const raw = col.render ? col.render(row) : (row as Record<string, unknown>)[col.key];
  if (isFmTableStructuredCell(raw)) {
    return renderFmTableCell(raw, { emptyLabel, locale });
  }
  return raw as React.ReactNode;
}

export function TableCell<T>({
  row,
  col,
  usesLabelledCells,
  emptyCellLabel,
  locale,
  actionLabels,
}: CellProps<T>): React.ReactElement {
  const value = resolveCellValue(row, col, actionLabels, emptyCellLabel ?? '', locale);
  // Governed action cells own their own rail; skip the em-dash empty treatment.
  const rendered = col.actions ? value : renderCellValue(value, emptyCellLabel);

  return (
    <td
      key={col.key}
      className={columnClassName(col, 'fm-table__cell')}
      style={{ textAlign: col.numeric ? 'right' : col.align }}
      data-label={
        usesLabelledCells
          ? columnLabel(col.mobileLabel ?? col.label ?? col.header, col.key)
          : undefined
      }
    >
      {rendered}
    </td>
  );
}

function renderDataValue<T>(
  row: T,
  col: FmTableColumn<T>,
  dataValueRule: NonNullable<ReturnType<typeof resolveDataValueRule<T>>>,
): React.ReactNode {
  const { dataTestId, ...dataValueProps } = resolveDataValueProps(row, col);
  return <FmDataValue rule={dataValueRule} {...dataValueProps} data-testid={dataTestId} />;
}

export interface FooterProps<T> {
  a11yLabels: FmTableA11yLabels;
  loading: boolean;
  footerRow?: T;
  summaryContent: React.ReactNode;
  totalColumnCount: number;
  showExpandColumn: boolean;
  columns: FmTableColumn<T>[];
  footerRowClassName?: FmTableProps<T>['footerRowClassName'];
  footerRowTestId?: FmTableProps<T>['footerRowTestId'];
  renderCell: (row: T, col: FmTableColumn<T>) => React.ReactNode;
}

export function TableFooter<T>({
  a11yLabels,
  loading,
  footerRow,
  summaryContent,
  totalColumnCount,
  showExpandColumn,
  columns,
  footerRowClassName,
  footerRowTestId,
  renderCell,
}: FooterProps<T>): React.ReactElement | null {
  if (loading) return null;
  const hasSummary = shouldRenderSummary(summaryContent);
  if (footerRow === undefined && !hasSummary) return null;
  return (
    <tfoot>
      {footerRow !== undefined ? (
        <tr
          className={cx('fm-table__row', 'fm-table__footer-row', footerRowClassName?.(footerRow))}
          data-testid={footerRowTestId?.(footerRow)}
        >
          {showExpandColumn ? <td className="fm-table__cell fm-table__expand-cell" data-label="" aria-label={a11yLabels.expandRow} /> : null}
          {columns.map((col) => renderCell(footerRow, col))}
        </tr>
      ) : null}
      {hasSummary ? (
        <tr className="fm-table__summary-row">
          <td colSpan={totalColumnCount}>
            <div className="fm-table__summary" data-testid="fm-table-summary">{summaryContent}</div>
          </td>
        </tr>
      ) : null}
    </tfoot>
  );
}

export interface BodyRowsProps<T extends object> {
  a11yLabels: FmTableA11yLabels;
  loading: boolean;
  internalRows: FmTableInternalRow<T>[];
  columns: FmTableColumn<T>[];
  totalColumnCount: number;
  showExpandColumn: boolean;
  usesLabelledCells: boolean;
  emptyText?: React.ReactNode;
  groupHeader?: FmTableProps<T>['groupHeader'];
  groupCountLabel?: FmTableProps<T>['groupCountLabel'];

  renderDataRow: (record: Extract<FmTableInternalRow<T>, { kind: 'data' }>) => React.ReactNode;
}

export function TableBodyRows<T extends object>({
  a11yLabels,
  loading,
  internalRows,
  columns,
  totalColumnCount,
  showExpandColumn,
  usesLabelledCells,
  emptyText,
  groupHeader,
  groupCountLabel,
  renderDataRow,
}: BodyRowsProps<T>): React.ReactNode {
  if (loading) {
    return Array.from({ length: 5 }, (_, rowIndex) => (
      <tr key={`fm-table-skeleton-${rowIndex}`} className="fm-table__row fm-table__skeleton-row" data-testid="fm-table-skeleton-row">
        {showExpandColumn ? <td className="fm-table__cell fm-table__expand-cell" data-label="" aria-label={a11yLabels.expandRow} /> : null}
        {columns.map((col) => (
          <td
            key={col.key}
            className={columnClassName(col, 'fm-table__cell')}
            data-label={
        usesLabelledCells
          ? columnLabel(col.mobileLabel ?? col.label ?? col.header, col.key)
          : undefined
      }
          >
            <FmSkeleton rows={1} showTitle={false} style={{ width: '75%', minWidth: 48 }} />
          </td>
        ))}
      </tr>
    ));
  }

  if (internalRows.length === 0) {
    return (
      <tr className="fm-table__empty-row">
        <td className="fm-table__empty-state-cell" colSpan={totalColumnCount}>
          <FmEmpty description={emptyText} data-testid="fm-table-empty" />
        </td>
      </tr>
    );
  }

  return internalRows.map((record) => {
    if (!isGroupRow(record)) return renderDataRow(record);
    return (
      <tr key={record.key} className="fm-table__group-row">
        <th className="fm-table__group-cell" colSpan={totalColumnCount} scope="colgroup">
          {renderGroupHeader(record.group, groupHeader, groupCountLabel)}
        </th>
      </tr>
    );
  });
}

export interface ErrorStateProps {
  caption?: React.ReactNode;
  tableId: string;
  tableClass: string;
  errorShellTestId: string;
  error: React.ReactNode;
  style?: React.CSSProperties;
}

export function TableErrorState({
  caption,
  tableId,
  tableClass,
  errorShellTestId,
  error,
  style,
}: ErrorStateProps): React.ReactElement {
  return (
    <div className={tableClass} data-testid={errorShellTestId} data-fm-table-state="error" style={style}>
      {caption ? <div id={`${tableId}-caption`} className="fm-table__caption" data-testid="fm-table-caption">{caption}</div> : null}
      <FmBanner tone="error" title={error} data-testid="fm-table-error" />
    </div>
  );
}
